// Cuentas bancarias de la EMPRESA (pedido de Rodrigo, 2026-10-19): desde cuál se transfiere a un
// proveedor y con cuál cuenta corriente se gira el cheque. No se borran (los pagos las nombran): se
// desactivan. Registrarlas pide el permiso de autorizar pagos.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { useSesion } from '../sesion/SesionContexto';
import {
  clavesPagos,
  NOMBRE_TIPO_CUENTA,
  useCuentasEmpresa,
  type CuentaEmpresa,
} from './datosPagos';
import { SelectorBanco, useBancos } from '../comun/bancos';

export function CuentasBancarias() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const cuentas = useCuentasEmpresa(empresaId);
  const bancos = useBancos();
  const gestiona = puede('tesoreria.pago.autorizar');
  const [banco, setBanco] = useState('');
  const [tipoCuenta, setTipoCuenta] = useState<CuentaEmpresa['tipoCuenta']>('CORRIENTE');
  const [numero, setNumero] = useState('');
  const [alias, setAlias] = useState('');
  const refrescar = () => qc.invalidateQueries({ queryKey: clavesPagos.cuentas(empresaId) });

  const crear = useMutation({
    mutationFn: () =>
      api.post('/tesoreria/cuentas-bancarias', {
        empresaId,
        banco: banco.trim(),
        tipoCuenta,
        numero: numero.trim(),
        alias: alias.trim() || null,
      }),
    onSuccess: async () => {
      setBanco('');
      setNumero('');
      setAlias('');
      await refrescar();
    },
  });
  const cambiar = useMutation({
    mutationFn: (c: CuentaEmpresa) =>
      api.patch(`/tesoreria/cuentas-bancarias/${c.id}`, { version: c.version, activa: !c.activa }),
    onSuccess: refrescar,
  });

  return (
    <div className="pantalla consulta cuentas-bancarias" data-pantalla="cuentas-bancarias">
      <header className="encabezado-entidad">
        <span className="ruta-migas">Pago a Proveedores › Cuentas bancarias</span>
        <h2>Cuentas bancarias de la empresa</h2>
      </header>
      <p className="apagado">
        Desde estas cuentas se transfiere a los proveedores y con las cuentas corrientes se giran
        los cheques. Las cuentas de cada cliente o proveedor se registran en su ficha.
      </p>
      {gestiona && (
        <section className="paso-cobro" aria-label="Nueva cuenta">
          <div className="barra-filtros datos-pago">
            <label className="campo">
              <span className="etiqueta">Banco</span>
              <SelectorBanco etiqueta="Banco" valor={banco} alCambiar={(v) => setBanco(v)} />
            </label>
            <label className="campo">
              <span className="etiqueta">Tipo</span>
              <select
                aria-label="Tipo de cuenta"
                value={tipoCuenta}
                onChange={(e) => setTipoCuenta(e.target.value as CuentaEmpresa['tipoCuenta'])}
              >
                {(['CORRIENTE', 'VISTA', 'AHORRO'] as const).map((t) => (
                  <option key={t} value={t}>
                    {NOMBRE_TIPO_CUENTA[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">N° de cuenta</span>
              <input
                aria-label="Número de cuenta"
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
              />
            </label>
            <label className="campo">
              <span className="etiqueta">Nombre corto (opcional)</span>
              <input
                aria-label="Alias"
                placeholder="Ej.: Proveedores"
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="primario"
              disabled={banco.trim().length < 2 || numero.trim().length < 3 || crear.isPending}
              onClick={() => crear.mutate()}
            >
              Agregar cuenta
            </button>
          </div>
          {crear.isError && <PanelError error={crear.error} />}
        </section>
      )}
      <table className="grilla compacta" aria-label="Cuentas bancarias">
        <thead>
          <tr>
            <th>Banco</th>
            <th>Tipo</th>
            <th>N° de cuenta</th>
            <th>Nombre corto</th>
            <th>Estado</th>
            {gestiona && <th />}
          </tr>
        </thead>
        <tbody>
          {(cuentas.data ?? []).map((c) => (
            <tr key={c.id} className={c.activa ? '' : 'apagado'}>
              <td>{c.banco}</td>
              <td>{NOMBRE_TIPO_CUENTA[c.tipoCuenta] ?? c.tipoCuenta}</td>
              <td className="mono">{c.numero}</td>
              <td>{c.alias ?? ''}</td>
              <td>{c.activa ? 'Activa' : 'Inactiva'}</td>
              {gestiona && (
                <td>
                  <button
                    type="button"
                    className="boton-mini"
                    disabled={cambiar.isPending}
                    onClick={() => cambiar.mutate(c)}
                  >
                    {c.activa ? 'Desactivar' : 'Activar'}
                  </button>
                </td>
              )}
            </tr>
          ))}
          {cuentas.isSuccess && !cuentas.data.length && (
            <tr>
              <td colSpan={6} className="apagado">
                Todavía no hay cuentas registradas.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {cuentas.isError && <PanelError error={cuentas.error} />}
      <details className="maestro-bancos">
        <summary>Maestro de bancos ({bancos.data?.length ?? 0})</summary>
        <table className="grilla compacta" aria-label="Maestro de bancos">
          <thead>
            <tr>
              <th>Código</th>
              <th>Banco</th>
              <th>Razón social</th>
              <th>Otras marcas</th>
            </tr>
          </thead>
          <tbody>
            {(bancos.data ?? []).map((b) => (
              <tr key={b.codigo}>
                <td className="mono">{/^\d+$/.test(b.codigo) ? b.codigo : '—'}</td>
                <td>{b.nombre}</td>
                <td>{b.razonSocial}</td>
                <td>{b.marcas ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      {cambiar.isError && <PanelError error={cambiar.error} />}
    </div>
  );
}
